import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import { Typography, Box } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import clsx from "clsx";
import { PATHS } from "util/appConstants";
import Navbar from "components/Navbar";
import DarkLayout from "components/Shared/DarkLayout";
import CheckIcon from "@material-ui/icons/Check";
import XIcon from "@material-ui/icons/Clear";
import AdminBar from "components/Masterbar/AdminBar";
import {
  getAccountPermissions,
  selectAccountPermission,
  selectAccountPermissionsStatus,
} from "redux/slices/accountPermissionsSlice";
import { selectUser } from "redux/slices/userSlice";

const useStyles = makeStyles({
  _heading: {
    color: "#F5F5F5",
    font: "normal normal normal 28px/40px Questrial",
  },
  _edit: {
    color: "#6F9CEB",
    width: "22px",
    height: "22px",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      transform: "scale(1.3)",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _editbox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  _infoheading: {
    color: "#F5F5F5",
    font: "normal normal medium 22px/32px Roboto",
    marginTop: "44px",
  },
  _head: {
    color: "#F5F5F5",
    opacity: "0.6",
    font: "normal normal normal 12px/24px Roboto",
    marginRight: "32px",
  },
  _basicdetailhead: {
    display: "flex",
    marginTop: "41px",
  },
  _basicdetail: {
    display: "flex",
  },
  _detail: {
    color: "#F5F5F5",
    marginRight: "32px",
    font: "normal normal normal 18px/24px Roboto",
  },
  _name: {
    letterSpacing: "0px",
  },
  _margintop80: {
    marginTop: "80px",
  },
  _adressbox: {
    marginTop: "24px",
  },
  _buttonbox: {
    display: "flex",
    margin: "0 16px",
  },
  _edittext: {
    width: "24px",
    height: "16px",
    color: "#6F9CEB",
    font: "normal normal normal 14px / 20px Roboto",
    padding: "4px 8px",
    display: "none",
    position: "absolute",
    marginLeft: "20px",
    transition: "all 0.3s ease-in-out",
  },
});

const AccountPermissions = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(selectUser);
  const loading = useSelector(selectAccountPermissionsStatus);
  const accountPermissions = useSelector(selectAccountPermission);
  useEffect(() => {
    dispatch(getAccountPermissions());
  }, [dispatch]);

  const permissions = accountPermissions?.permissions || {};
  const allowAtbasEditing =
    permissions?.allow_atbas_order_editing === true ||
    permissions?.allow_atbas_order_editing === 1 ||
    permissions?.allow_atbas_order_editing === "1" ||
    permissions?.allow_atbas_order_editing === "true";

  const hasMobilePermission = permissions?.create_driver || permissions?.delivery_app_doc || false;
  const hasDeliveryDocPermission =
    permissions?.export_sorting_zipcode ||
    permissions?.export_sorting_priority ||
    permissions?.export_sorting_routing ||
    permissions?.export_sorting_alphabetical || false;

  const getSortingType = () => {
    if (permissions?.export_sorting_zipcode) return "Zipcode";
    if (permissions?.export_sorting_priority) return "Priority";
    if (permissions?.export_sorting_routing) return "Routing";
    if (permissions?.export_sorting_alphabetical) return "Alphabetical";
    return null;
  };

  const sortingType = getSortingType();

  const includeDeliveryDoc = permissions?.delivery_app_doc || false;

  const editHandler = () => {
    history.push(PATHS.admin.accountPermissionEdit);
  };

  const formatRecipients = (recipients) => {
    if (!Array.isArray(recipients) || recipients.length === 0) return "-";
    const labelMap = {
      CUSTOMER_EMAIL: t("Customer Email"),
      SUPPLIER_EMAIL: t("Supplier Email"),
      SUPPLIER_LOCATION_EMAIL: t("Supplier Location Email"),
      ACCOUNT_EMAIL: t("Account Email"),
      CUSTOM: t("Custom"),
      "Customer Email": t("Customer Email"),
      "Supplier Email": t("Supplier Email"),
      "Supplier Location Email": t("Supplier Location Email"),
      "Account Email": t("Account Email"),
      Custom: t("Custom"),
    };
    return recipients.map((r) => labelMap[r] || r).join(", ");
  };

  return (
    <>
      <Navbar />
      <AdminBar />
      <DarkLayout doublebar loading={loading || !accountPermissions}>
        <div className={classes._editbox}>
          <Typography className={classes._heading} variant="h4">
            {accountPermissions?.SupplierGroup?.name || user?.SupplierGroup?.name}
          </Typography>

          <Box className={classes._buttonbox} component="div">
            <EditIcon onClick={editHandler} className={classes._edit} />
            <Typography component="span" className={clsx(classes._edittext, "edittag")}>
              {t("Edit")}
            </Typography>
          </Box>
        </div>

        <Typography className={classes._infoheading} variant="h5">
          {t("Basic Data")}
        </Typography>

        <div className={classes._basicdetailhead}>
          <div>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Account")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {accountPermissions?.SupplierGroup?.name || user?.SupplierGroup?.name}
            </Typography>
          </div>
          <div style={{ marginLeft: 32 }}>
            <Typography className={clsx(classes._head, classes._name)} variant="h6">
              {t("Account Email")}
            </Typography>
            <Typography className={clsx(classes._name, classes._detail)} variant="h6">
              {accountPermissions?.email_settings?.account_email || accountPermissions?.email_settings?.email || "-"}
            </Typography>
          </div>
        </div>

        <Typography className={classes._infoheading} variant="h5">
          {t("Tour Setting")}
        </Typography>

        <Box mt={3}>
          <Typography className={clsx(classes._head)} variant="h6">
            {t("Delivery Method")}
          </Typography>
          <Box display="flex" flexWrap="wrap" mt={1}>
            {hasMobilePermission && (
              <Box mr={4} mb={2}>
                <Typography className={classes._detail}>
                  <CheckIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {t("Mobile")}
                </Typography>
              </Box>
            )}
            {hasDeliveryDocPermission && (
              <Box mr={4} mb={2}>
                <Typography className={classes._detail}>
                  <CheckIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {t("Delivery Document")}
                </Typography>
              </Box>
            )}
            {!hasMobilePermission && !hasDeliveryDocPermission && (
              <Box mr={4} mb={2}>
                <Typography className={classes._detail}>
                  <CheckIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {t("None")}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {hasDeliveryDocPermission && sortingType && (
          <Box mt={3}>
            <Typography className={clsx(classes._head)} variant="h6">
              {t("Document Sorting Type")}
            </Typography>
            <Typography className={classes._detail}>
              {sortingType}
            </Typography>
          </Box>
        )}

        {hasMobilePermission && (
          <Box mt={3}>
            <Typography className={clsx(classes._head)} variant="h6">
              {t("Include Delivery Document")}
            </Typography>
            <Typography className={classes._detail}>
              {includeDeliveryDoc ? (
                <CheckIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              ) : (
                <XIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
              )}
              {t("Include delivery document with efficient sorting")}
            </Typography>
          </Box>
        )}

        <Box mt={3}>
          <Typography className={clsx(classes._head)} variant="h6">
            {t("allow_atbas_order_editing")}
          </Typography>
          <Typography className={classes._detail}>
            {allowAtbasEditing ? (
              <CheckIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            ) : (
              <XIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            )}
            {allowAtbasEditing ? t("Enabled") : t("Disabled")}
          </Typography>
        </Box>

        <Typography className={classes._infoheading} variant="h5">
          {t("Email Configuration")}
        </Typography>

        <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("SMTP Settings")}
          </Typography>
          <Typography className={classes._detail}>
            {t('Host')}: {accountPermissions?.email_settings?.smtp?.host || "-"}
          </Typography>
          <Typography className={classes._detail}>
            {t('Email')}: {accountPermissions?.email_settings?.smtp?.email || "-"}
          </Typography>
          <Typography className={classes._detail}>
            {t('Name')}: {accountPermissions?.email_settings?.smtp?.name || "-"}
          </Typography>
          <Typography className={classes._detail}>
            {t('Port')}: {accountPermissions?.email_settings?.smtp?.port || "-"}
          </Typography>
          <Typography className={classes._detail}>
            {t('Security')}: {accountPermissions?.email_settings?.smtp?.security || "-"}
          </Typography>
        </Box>
        <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("Route Started")}
          </Typography>
          <Typography className={classes._detail}>
            {t("To")}: {formatRecipients(accountPermissions?.email_settings?.route_started?.to)}
          </Typography>
          <Typography className={classes._detail}>
            {t("CC")}: {formatRecipients(accountPermissions?.email_settings?.route_started?.cc)}
          </Typography>
        </Box>

        <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("Stop Skipped")}
          </Typography>
          <Typography className={classes._detail}>
            {t("To")}: {formatRecipients(accountPermissions?.email_settings?.stop_skipped?.to)}
          </Typography>
          <Typography className={classes._detail}>
            {t("CC")}: {formatRecipients(accountPermissions?.email_settings?.stop_skipped?.cc)}
          </Typography>
        </Box>

        <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("Unscanned Packages")}
          </Typography>
          <Typography className={classes._detail}>
            {t("To")}: {formatRecipients(accountPermissions?.email_settings?.unscanned_packages?.to)}
          </Typography>
          <Typography className={classes._detail}>
            {t("CC")}: {formatRecipients(accountPermissions?.email_settings?.unscanned_packages?.cc)}
          </Typography>
        </Box>
        <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("Pathway Status")}
          </Typography>
          <Typography className={classes._detail}>
            {t("To")}: {formatRecipients(accountPermissions?.email_settings?.pathway_status?.to)}
          </Typography>
          <Typography className={classes._detail}>
            {t("CC")}: {formatRecipients(accountPermissions?.email_settings?.pathway_status?.cc)}
          </Typography>
        </Box>
        <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("Order Created")}
          </Typography>
          <Typography className={classes._detail}>
            {t("To")}: {formatRecipients(accountPermissions?.email_settings?.order_created?.to)}
          </Typography>
          <Typography className={classes._detail}>
            {t("CC")}: {formatRecipients(accountPermissions?.email_settings?.order_created?.cc)}
          </Typography>
        </Box>
        <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("Return Order Unsuccessful")}
          </Typography>
          <Typography className={classes._detail}>
            {t("To")}: {formatRecipients(accountPermissions?.email_settings?.return_order_unsuccessful?.to)}
          </Typography>
          <Typography className={classes._detail}>
            {t("CC")}: {formatRecipients(accountPermissions?.email_settings?.return_order_unsuccessful?.cc)}
          </Typography>
        </Box>

        <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("Lost Packages Report")}
          </Typography>
          <Typography className={classes._detail}>
            {t("To")}: {formatRecipients(accountPermissions?.email_settings?.lost_packages_report?.to)}
          </Typography>
          <Typography className={classes._detail}>
            {t("CC")}: {formatRecipients(accountPermissions?.email_settings?.lost_packages_report?.cc)}
          </Typography>
        </Box>
        {/* todo: enable when email template is available */}
        {/* <Box mt={3}>
          <Typography className={classes._head} variant="h6">
            {t("Switch to Manual Marking")}
          </Typography>
          <Typography className={classes._detail}>
            {t("To")}: {accountPermissions?.email_settings?.switch_to_manual_marking?.to?.join(", ") || "-"}
          </Typography>
          <Typography className={classes._detail}>
            {t("CC")}: {accountPermissions?.email_settings?.switch_to_manual_marking?.cc?.join(", ") || "-"}
          </Typography>
        </Box> */}
      </DarkLayout >
    </>
  );
};

export default AccountPermissions;
